// Extra CMS slots for the 'how_it_works' page — added to make previously-hardcoded
// public marketing copy editable. Concatenated into CMS_SLOTS via ./index.
// Each default MUST equal the exact copy the component rendered before wiring,
// so the public page is byte-identical until an admin edits it.
import type { SlotDef } from '../slots';

/**
 * Per-step screenshot slots for the two flows.
 *
 * One image slot per step position, in step order. These are POSITIONAL: image
 * N pairs with step N of the flow's `step_list`. Reordering the steps in the
 * editor therefore re-pairs the images, which is why each label names its
 * position and each helpText says so out loud. The alternative — carrying the
 * image inside each `StepItem` — would mean a new slot kind and an editor
 * rewrite for one page, so the positional pairing is deliberate and stated.
 *
 * ⚠️ EVERY DEFAULT IS BLANK, deliberately (2026-08-18). These used to point at
 * committed captures of interface fragments — a crop of the actual screen each
 * step described. Richie asked for them gone and the page to be plain cards:
 * he would rather use photography here, and the fragments were carrying a lot
 * of empty ground even after being cropped to content.
 *
 * The SLOTS stay, because he still wants to add his own images — including one
 * for the exchange card, which is step 5. A blank url renders the step as a
 * card with no image, so the page is cards until he uploads, and each upload
 * appears on its own without any code change.
 *
 * The retired fragment captures, their Storybook stories and the vignette
 * module that drew them were deleted in the same commit; recover from git
 * history if the decision is ever reversed.
 */
function stepShot(flow: 'artist' | 'host', n: number, url: string): SlotDef<'image'> {
  const who = flow === 'artist' ? 'Artist' : 'Host';
  return {
    key: `how_it_works.${flow}_step_${n}_image`,
    page: 'how_it_works',
    kind: 'image',
    label: `${who} flow: step ${n} screenshot`,
    helpText:
      `The image shown with step ${n} of the ${flow} flow. Blank by default, which renders the ` +
      `step as a plain card — upload one to add it. Pairs with whichever step sits in position ` +
      `${n}, so re-ordering the steps re-pairs the images. Leave alt blank — the step title ` +
      'already describes it.',
    default: { url, alt: '' },
  };
}

export const howItWorksExtra: SlotDef[] = [
  // ─── The Smart Rider (moved here from the homepage, 2026-08-10) ────────
  // On the homepage this sat between "how it works" and the founding offer with
  // nothing around it to explain what a confirmed exchange was, so it read as an
  // abstract feature demo (Richie, feedback DYFqwfvei8). Here it lands after both
  // flows have walked the reader through a request, a confirm and a stay.
  {
    key: 'how_it_works.rider_eyebrow',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Smart Rider: eyebrow',
    default: 'The one thing we generate',
  },
  {
    key: 'how_it_works.rider_title',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Smart Rider: title',
    default: 'The Smart Rider.',
  },
  {
    key: 'how_it_works.rider_intro',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Smart Rider: intro paragraph',
    helpText: 'The example rider below it is a fixed illustration and is not editable here.',
    default:
      'Built from fields you and the host already filled in, no second form. Shared in the thread the moment the exchange is confirmed, and re-built whenever a detail changes.',
  },

  // ─── Per-step screenshots ──────────────────────────────────────────────
  // Six positions per flow, matching the six steps each flow currently holds.
  stepShot('artist', 1, ''),
  stepShot('artist', 2, ''),
  stepShot('artist', 3, ''),
  stepShot('artist', 4, ''),
  // Step 5 of both flows is the stay itself — the artist arrives, plays, is fed
  // and housed. There is no screen for that, and inventing one would be the
  // product claiming credit for the part that happens off it. Blank by default;
  // an admin can still put a photo here if they want one.
  stepShot('artist', 5, ''),
  stepShot('artist', 6, ''),
  stepShot('host', 1, ''),
  stepShot('host', 2, ''),
  stepShot('host', 3, ''),
  stepShot('host', 4, ''),
  stepShot('host', 5, ''),
  stepShot('host', 6, ''),

  // ─── Flow CTAs (the sign-up nudge after each flow) ─────────────────────
  {
    key: 'how_it_works.artist_cta_label',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Artist flow: button label',
    helpText: 'The "→" arrow is added automatically. Links to sign-up (fixed).',
    default: 'Sign up as an artist',
  },
  {
    key: 'how_it_works.host_cta_label',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Host flow: button label',
    helpText: 'The "→" arrow is added automatically. Links to sign-up (fixed).',
    default: 'List your venue',
  },
  // ─── Fine-print CTAs ───────────────────────────────────────────────────
  {
    key: 'how_it_works.fine_print_cta_primary',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Fine print: primary button',
    helpText: 'Links to sign-up (fixed).',
    default: 'Sign up',
  },
  {
    // Renamed 2026-08-18 with the page itself: "Read the promise" sent readers
    // to a page now called Our Ethos, and one that no longer carries the
    // promise text. The destination (/what-we-are-and-arent) is unchanged.
    key: 'how_it_works.fine_print_cta_secondary',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Fine print: secondary link',
    helpText: 'Links to the Our Ethos page (fixed). Include the arrow if you want it.',
    default: 'Read our ethos →',
  },
  // ─── Keep reading: closing tile block ──────────────────────────────────
  {
    key: 'how_it_works.next_steps_title',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Keep reading: title',
    default: 'Keep reading.',
  },
  {
    key: 'how_it_works.next_steps_lede',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Keep reading: lede',
    default: 'Once you know the flow, the next pages explain the principles behind it.',
    // Closing-block subheading removed site-wide at Richie's request — no longer
    // rendered. Retired (not deleted) so any override doesn't orphan; drops out
    // of the admin CMS editor.
    deprecated: true,
  },
  {
    key: 'how_it_works.next_steps_tile_1_title',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Keep reading: tile 1 title',
    helpText: 'Links to the Promise page (fixed). Only the title/description are editable.',
    default: 'The WWAAVE Promise',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'how_it_works.next_steps_tile_1_desc',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Keep reading: tile 1 description',
    default: 'No cuts, no premium-placement paywalls, no data sales. What WWAAVE is and isn’t.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'how_it_works.next_steps_tile_2_title',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Keep reading: tile 2 title',
    helpText: 'Links to the Founding venues page (fixed). Only the title/description are editable.',
    default: 'Founding venues',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'how_it_works.next_steps_tile_2_desc',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Keep reading: tile 2 description',
    helpText: 'The "$19 AUD/month" price is copy here — update it if the live price changes.',
    default:
      'First 50 venues, free for the first year, then $19 AUD/month locked for as long as they’re a member.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'how_it_works.next_steps_tile_3_title',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Keep reading: tile 3 title',
    helpText: 'Links to the FAQ page (fixed). Only the title/description are editable.',
    default: 'FAQ',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'how_it_works.next_steps_tile_3_desc',
    page: 'how_it_works',
    kind: 'markdown',
    label: 'Keep reading: tile 3 description',
    default: 'Five questions we hear most: free?, verification, disputes, reviews.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
];
